/**
 * Every word the Studio says, in one table -- STUDIO_DESIGN.md 6.8 (site voice).
 *
 * ONE table, on the `pdf-editor/views/notices.copy.ts` precedent, for two reasons the PDF editor
 * learned the hard way: a sentence written at its call site drifts from the one in the design, and
 * a QA row cannot address a string that has no name. Every row here is either a constant or a
 * FUNCTION of the numbers it mentions -- never a template a caller fills in, because that puts the
 * grammar at the call site again.
 *
 * Numbers come from the limits, never from a literal (D11: 6.8 says "24 clips" and the shipped cap
 * is 30; the schema, `StudioLimits` and this table now move together).
 */
import { STUDIO_VIDEO_CODEC_LABEL } from '../export/output-format';
import { MAX_TRANSITION_MS } from '../model/composition';
import type { Transition } from '../model/composition';
import { FRAME_MAX } from '../model/frame';
import { STUDIO_LIMITS } from '../model/limits';
import { STUDIO_TTL_HOURS } from '../state/persistence';

/**
 * IT1-W1 (copy 4): ONE duration formatter, FLOORED to tenths -- the same reading `clockTenths` gives
 * the transport. `Math.round` here beside a floored clock put "15.0 s" on the tile and "0:14.9" in
 * the inspector for one 14 997 ms clip. Exported as `secondsTenths` for the tile and the trim readout
 * (`clip.component.tsx` -- NEEDS W2: route its three `toFixed(1)` through it).
 */
export function secondsTenths(ms: number): string {
    return (Math.floor(Math.max(0, ms) / 100) / 10).toFixed(1);
}

const seconds = secondsTenths;

/** A RANGE's floor is said rounded UP: "Between 1.1 and 60.0" for a 1 062.5 ms minimum -- 1.0 would be refused. */
function secondsCeil(ms: number): string {
    return (Math.ceil(Math.max(0, ms) / 100) / 10).toFixed(1);
}

/** m:ss, as every duration on screen is written (the artboards: 0:24, 0:06.4, 20:40). */
export function clock(ms: number): string {
    const total = Math.max(0, Math.round(ms / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * m:ss.t -- the transport's own stamp (the artboards put 0:06.4 / 0:24.0 under the preview).
 *
 * It does NOT compose `clock` with a tenths digit, and the reason is arithmetic: `clock` ROUNDS to
 * the nearest second, so 6 600 ms would read "0:07.6" -- a time that runs backwards as the
 * playhead crosses each half-second. The seconds here are FLOORED, like every other running clock
 * on earth, and the tenth is the same number's own first decimal.
 */
export function clockTenths(ms: number): string {
    const safe = Math.max(0, ms);
    const total = Math.floor(safe / 1000);
    const tenth = Math.floor((safe % 1000) / 100);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}.${tenth}`;
}

/**
 * One label per WIRE transition type, typed as a total `Record` so a seventh type added to the
 * schema stops compiling here rather than rendering a blank chip. The pre-flight scan found the
 * header naming six and the transition control offering four (row 40); both read this.
 */
const TRANSITION_LABELS: Readonly<Record<Transition['type'], string>> = Object.freeze({
    NONE: 'None',
    CROSSFADE: 'Cross-fade',
    SLIDE_LEFT: 'Slide left',
    SLIDE_RIGHT: 'Slide right',
    WIPE_LEFT: 'Wipe left',
    WIPE_RIGHT: 'Wipe right',
});

/**
 * The artboard's second line under each shape -- where the shape is USED, which is the only thing
 * that makes 9:16 mean anything to someone who has never posted a Reel.
 *
 * Declared here with its type rather than cast inside the table: a cast to
 * `Record<string, string>` said every id had a hint while `classic` had none, so
 * `frameShapeHints['classic']` type-checked as a string and was `undefined` on screen (review M2).
 * It is keyed by `FramePreset.id` and now covers every preset that has a ratio; `original` takes
 * the first photo's own shape instead, which the control computes.
 */
const FRAME_SHAPE_HINTS: Readonly<Record<string, string>> = Object.freeze({
    landscape: 'YouTube, TV',
    portrait: 'Reels, Shorts',
    square: 'Feed',
    social: 'Portrait post',
    classic: 'Slides, older TVs',
});

/**
 * R14-S18 (UX2 R14-U34): ONE sentence for a file that decodes NOWHERE -- the gate, the header's reason, the
 * phone's summary row, the stage chip, the announcer and the tablet pane all say this. UX2 counted four
 * ("remove it or add another file" / "add it again" / "add this file again" / "Add it again to export"): the
 * gate said REMOVE while the row said ADD AGAIN, and for a text file named `.jpg` adding it again changes
 * nothing. "Add it again" survives ONLY for the interruption family (`INTERRUPTION_COPY.uploadsLost`,
 * `uploadCutShort`), where the bytes never landed and adding them again is the remedy.
 */
function clipUnreadableGate(name: string): string {
    return `We couldn’t read ${name} — remove it.`;
}

/** The same sentence over several names: "We couldn’t read a.jpg, b.mp4 — remove them." */
function clipsUnreadableGate(names: readonly string[]): string {
    return names.length === 1 ? clipUnreadableGate(names[0]) : `We couldn’t read ${names.join(', ')} — remove them.`;
}

/** One phrase, two rows: the chip's whole text and the second half of the summary line. */
function notPlaced(n: number): string {
    return `${n} not yet placed`;
}

/**
 * The SCOPED keys the inspector's "?" documents (S-21), as strings: `views/keyboard.ts` re-exports
 * this as `TRACK_KEYS` so the tooltip, the QA matrix and the e2e still read one source.
 */
const TRACK_KEYS_TABLE: readonly { keys: string; label: string }[] = Object.freeze([
    { keys: '← / →', label: 'Select the previous or next clip' },
    { keys: 'Alt + ← / →', label: 'Move the clip earlier or later' },
    { keys: 'Shift + ← / →', label: 'Change the length by one frame' },
    // IT1-W2 (fe #6): the tile's keyboard trim of the START edge (video clips) -- the pointer handle's twin.
    { keys: 'Shift + Alt + ← / →', label: 'Trim the start of a clip by one frame' },
    { keys: 'Home / End', label: 'Select the first or last clip' },
    { keys: 'Delete / Backspace', label: 'Remove the clip (undo puts it back)' },
    { keys: 'Ctrl/⌘ + D', label: 'Duplicate the clip' },
    { keys: 'Enter', label: 'Open the clip (phone)' },
    { keys: 'Esc', label: 'Cancel a drag or a nudge' },
]);

/**
 * Round 9 (L1 #12): ONE voice for "expired". R8 QA counted four -- toast "Your files expired — files
 * are kept for 2.5 hours…", tile "File gone — re-add" (clipped to "ile gone — re-add" on a 96 px
 * phone tile), tray head "Files are kept 2.5 hours…", stage "File expired — add it again". Every
 * surface now reads from here: the SENTENCE (`clip`) on the stage chip, the tray row and L2's
 * inspector card; the same two words (`badge`) on the tile, whose 96 px at 390 hold no more; the
 * TTL sentences for the tray head and the two toasts. `COPY.*`, `TRAY_COPY.*` and `R8_COPY.*` keep
 * their names as aliases -- 40+ call sites and specs address them -- and all point here.
 */
export const EXPIRED_COPY = Object.freeze({
    /** The stage chip, the tray row, the inspector's card: the one sentence. */
    clip: 'File expired — add it again',
    /** The timeline tile (96 px wide on a phone): the same words, cut to what fits; the tooltip carries `clip`. */
    badge: 'File expired',
    /**
     * Round 10 (M1, R9 QA #12): the SAME sentence, plural -- R9 still counted three on one screen (toast
     * "Your files expired — files are kept for 2.5 hours. Add them again, or start over.", tray head
     * "Files are kept 2.5 hours. Add these again to export: …", chip "File expired — add it again").
     * The TTL fact stays with the whole-project case (`project`) and the header's "kept 2.5 h" chip.
     */
    many: 'Files expired — add them again',
    /** The tray's head over its expired rows: the sentence, what it blocks, and which files. */
    tray: (names: string[]) => `${names.length === 1 ? 'File expired — add it again' : 'Files expired — add them again'} to export: ${names.join(', ')}.`,
    /** The toast when files expire under a tab that stayed open (S3); its action key is "Start over". */
    files: 'Files expired — add them again.',
    /** The toast (and the empty stage) when the page's last project was swept on open (S3, L1 #5). */
    project: `Your last project expired — files are kept for ${STUDIO_TTL_HOURS} hours.`,
    /**
     * IT1-W1 (saas #1, P1): a `?sid=` whose record this browser does not hold -- a /downloads link
     * after the 2.5 h, another browser, cleared storage, a typo. The empty stage used to say nothing and
     * the URL was silently rewritten to a fresh id.
     *
     * R14-S9 (review 6 round 2, P2-05): it used to say "That project has expired — files are kept for 2.5
     * hours." -- a claim about TIME for an id this browser may never have held. The tab already tells the two
     * cases apart as far as they CAN be told: a record the mount's own sweep just deleted says `project`
     * ("Your last project expired…", the expired words); everything else -- an id nothing ever wrote, a
     * record swept on an EARLIER open, another browser's project -- reads back `null` from `readProject`
     * and nothing in localStorage can say which (the sweep deletes the record and its index entry; the TTL
     * leaves no tombstone). So this row is honest for every one of those: not found, with both reasons it
     * might be. Same voice as `project` (sentence case, an en dash, no number it cannot stand behind).
     */
    link: 'We couldn’t find that project — it may have expired or the link may be wrong.',
});

/** The refused-type line: names (two, then "and N more") and the uploader's own reason. */
function COPY_TRAY_REJECTED(names: string[], reason: string): string {
    const shown = names.slice(0, 2).join(', ') + (names.length > 2 ? ` and ${names.length - 2} more` : '');
    const said = reason.replace(/\.$/, '');
    return `${shown ? `${shown} — ` : ''}${said}. This file type can’t be added here; convert it to MP4, MP3 or JPG first.`;
}

export const COPY = {
    // --- shell + header -------------------------------------------------------------------
    title: 'Studio · Image to Video',
    titleShort: 'Studio',
    /**
     * Round 9 (L1 #11): short enough for the 1280/1440 header (was "Saved in this browser · files kept a few hou…").
     * R14-S3 (review-6 BUG-17): the ONE save line at every width -- the phone used to say "Saved in this
     * browser" (`savedOkShort`, gone) for the same state; this string is the shorter of the two anyway.
     */
    savedOk: `Saved · kept ${STUDIO_TTL_HOURS} h`,
    /** Round 12 (N2 O6): the save chip after a restore found the last export completed; the link word follows. */
    savedLastExport: 'Saved · last export on',
    savedLastExportLink: 'Downloads',
    /** Round 12 (N2 O3): the word on the inspector head's `?` key (its accessible name stays `hintsLabel`). */
    inspectorHelp: 'Help',
    savedSaving: 'Saving…',
    /** R3-JE-01 (review 7): the edits are written, a file is not -- never "Saved" beside a clip that needs its file again. */
    savedFileMissing: 'A file is missing — add it again',
    savedDisabled: "Not saved in this browser — don’t close this tab.",
    /** S3-06: the stale tab's save line agrees with its banner: saved elsewhere, reload to see it. */
    savedStale: 'Saved in another tab — reload to see the latest.',
    undo: 'Undo',
    redo: 'Redo',
    /** The two keys' tooltips (round 6, UX#22): the name and the shortcut, as every editor writes them. */
    undoTitle: 'Undo (Ctrl/⌘ + Z)',
    redoTitle: 'Redo (Ctrl/⌘ + Shift + Z)',
    /** S-33: the empty project's frame chip -- no size is known yet (also while the first photo is being read). */
    /* Owner ruling (review 7, F3): the toolbar key is "Video settings" -- the popover now holds more than the frame
       (Between clips). The size stays live in it; the popover's own subtitle keeps "First photo is W × H". */
    frameChipAuto: 'Video settings · auto',
    /** The same state on the phone chip, which has no room for the source (round 6, UX#18 / QA#19). */
    frameChipAutoShort: 'Video settings',
    /**
     * R14-S20 (UX3 R14-U49, for S21's `frame-control.component.tsx` / `header.component.tsx`): the Frame chip's ONE
     * grammar once a size is known -- `W × H · source`, and nothing else. UX3 counted eight spellings for one fact
     * ("Original · 3024 × 4032" after an explicit Original press, "3024 × 4032 · first photo" when auto, "Original 4:3",
     * "1080p 16:9", a bare "3024 × 4032"). The source is one of three words: `frameChipSourceFirstPhoto` while the frame
     * follows the first photo, the shape's ratio ("16:9", `shapeLabelFor`) while a preset shape is chosen, or
     * `frameChipSourceCustom` when the ratio matches no preset. "Original" is the Size row's key word, never a chip
     * word; "1080p" / "720p" are Size-row faces, never chip words. The capped first photo keeps IT1-W1's P1 word
     * (`frameSizeCappedHint`, "fits the first photo") as its source -- a scaled size is never called the photo's own.
     * The state with no size (`frameChipAuto`; `frameChipReading` was an orphan and retired, Sr review 4 #12) and the
     * phone's size-only short forms (`frameChipShort`, `frameChipPhone`, round 6 UX#18: no room for the source;
     * `frameChipShortTight`, Q27) stand outside this grammar.
     * The pre-U49 `frameChip(w, h, source)` and the Size row's `frameSizeOriginalHint` ("first photo") retired with
     * S21's 9cd1b61d8, which moved every sized chip state onto this formatter (no orphan keys).
     */
    frameChipLabel: (size: { width: number; height: number }, _source: string) => `Video settings · ${size.width} × ${size.height}`,
    /** U49: the chip's source while the frame is the first photo's own size (the same words the Size row's hint uses). */
    frameChipSourceFirstPhoto: 'first photo',
    /** U49: the chip's source when the frame's ratio matches no preset -- lower case, a source word, never the Size row's "Custom" face. */
    frameChipSourceCustom: 'custom',
    transitionLabels: TRANSITION_LABELS,
    transitionChip: (label: string, ms: number) => `Between clips · ${label} ${seconds(ms)} s`,
    /** The same chip where no duration applies -- one clip, or `NONE`. */
    transitionChipStatic: (label: string) => `Between clips · ${label}`,
    /**
     * Said only to a screen reader: the visible chip shows a clamped value without saying why. R14-S3
     * (review-6 BUG-17): "Longest allowed here: 1.9 seconds" had no referent -- the ceiling IS half the
     * shortest clip (`transitionCeilingMs`: min(2 s, ⌊(shortest − 63 ms) / 2⌋)), or the model's 2 s cap
     * when the clips are long enough for it; the note says which, from the ceiling alone (SR1 #13).
     */
    transitionCeilingNote: (ms: number) =>
        (ms >= MAX_TRANSITION_MS
            ? `Up to ${seconds(ms)} s here — the longest a transition can be`
            : `Up to ${seconds(ms)} s here — half the shortest clip`),
    /** The accessible name of the mobile pane switcher. */
    panesLabel: 'Studio panes',
    exportButton: 'Export MP4',
    chunkLoading: 'Loading the studio…',

    // --- empty state ----------------------------------------------------------------------
    /**
     * Round 13 (S, R12 QA N5): ONE verb and ONE noun set for the one action across the empty screen -- the
     * Files pane's key, the stage's key and the lane's tail all say "Add photos, clips or music" / "Add";
     * "browse" and "Select photos" are gone. "Drop" stays where a drop is real (the desktop title, the
     * desktop lane): it names the gesture, not a second way in.
     */
    emptyDropTitle: 'Drop photos, clips or music here',
    /**
     * Round 11 (S, R10 QA #2): the phone has no drag-and-drop, so its title does not say "drop". Round 13
     * (S, N5): the key under it carries the verb, so the title states the situation instead of repeating it.
     */
    emptyDropTitleCompact: 'Nothing on the timeline yet',
    emptyDropBody:
        'They play in the order you add them — 4 seconds each, one photo alone gets 8. Change any of it afterwards.',
    /** The stage's one primary: the same words as the Files pane's key (`trayAdd`) -- round 13 (S, N5). */
    emptySelect: 'Add photos, clips or music',
    emptyAccept:
        'JPG, PNG, WebP, HEIC, BMP, TIFF, GIF · clips MP4, MOV, WebM · music MP3, M4A, WAV',
    emptyVisualLane: 'Photos and clips go here — drop them anywhere',
    /** The same lane row on the phone: the "Add" key beside it is the way in (round 11, S, R10 QA #2). */
    emptyVisualLaneCompact: 'Photos and clips go here — tap Add',
    emptyMusicLane: 'A song from your device — optional',
    musicRights: 'Use music you have the rights to.',
    firstRun: [
        { n: '1', title: 'Add files', body: 'Photos, clips, one song.' },
        { n: '2', title: 'Arrange and time', body: 'Drag to reorder, pull an edge to change a length.' },
        { n: '3', title: 'Export MP4', body: 'Or skip straight here — the defaults are good.' },
    ],

    // --- files tray -----------------------------------------------------------------------
    trayHeading: 'Files',
    trayNotPlaced: notPlaced,
    /**
     * The line that stands in for the rows §6.2 takes away: "placed items leave it".
     *
     * `Main.dc.html`'s Files pane is the proof — a `1 not yet placed` chip and exactly ONE row
     * beside a timeline reading `Clip 2 of 6`. The six placed clips are a number here, not six
     * rows with thumbnails and delete buttons.
     */
    trayPlacedSummary: (placed: number, unplaced: number) =>
        [
            placed > 0 ? `${placed} on the timeline` : '',
            unplaced > 0 ? notPlaced(unplaced) : '',
        ].filter(Boolean).join(' · '),
    /**
     * S-10/S-16 (studio-fix): with auto-placement there is no "not yet placed" state. The tray
     * summary is ONE short line: what is still uploading, what could not be added. Callers pass
     * the counts they have; an all-zero call renders nothing.
     *
     * R14-S18 (UX2 R14-U38): a third count, files that CAN'T BE READ -- the chip read "1 uploading" for
     * `text.jpg`, refused by the add-time check at +40 ms, because its bytes were still going up. The caller
     * counts a file under ONE heading: unreadable first (`rendition === 'failed'`), else uploading, else
     * failed -- the gate's own order (`exportBlockedReason`).
     */
    traySummary: (uploading: number, failed: number, unreadable: number = 0) =>
        [
            uploading > 0 ? `${uploading} uploading` : '',
            unreadable > 0 ? `${unreadable} can’t be read` : '',
            failed > 0 ? `${failed} couldn’t be added` : '',
        ].filter(Boolean).join(' · '),
    /** S-10: the tray's row actions say what they do. */
    trayAddToTimeline: 'Add to timeline',
    trayTryAgain: 'Try again',
    /** S-10/S-35: the history label for one batch of files added together. */
    historyAddBatch: (photos: number, videos: number) => {
        const parts = [
            photos > 0 ? `${photos} photo${photos === 1 ? '' : 's'}` : '',
            videos > 0 ? `${videos} clip${videos === 1 ? '' : 's'}` : '',
        ].filter(Boolean);
        return `Add ${parts.join(' and ') || 'files'}`;
    },
    /** `SingleImage.dc.html`: one photo, named, with the size the frame default is taken from. */
    trayPlacedOne: (name: string, width: number, height: number) =>
        `${name} on the timeline · ${width} × ${height}`,
    trayAdd: 'Add photos, clips or music',
    /**
     * `SingleImage.dc.html`'s add control — the project already has its one photo. R14-S3 (review-6
     * BUG-18): it kept "photos" alone and the clips and music the empty key offered were gone from the
     * words once one photo landed; the key keeps the same three nouns as `trayAdd`.
     */
    trayAddMore: 'Add more photos, clips or music',
    trayAddHint: 'or drop them anywhere on this page',
    /**
     * `SingleImage.dc.html`'s hint, which replaces `trayAddHint` once a photo is placed: the
     * question a one-photo project asks is "what happens if I add another", not "where do I drop".
     * The "4 seconds each" is `DEFAULT_HOLD_MS`, guarded against it in `hold-defaults.spec.ts`.
     * R14-S3 (BUG-18): says what clips and music do too, so the sentence stays honest for all three.
     */
    trayOrderHint: 'Photos you add play one after another, 4 seconds each; clips and music keep their own length. Reorder them on the timeline.',
    trayRecent: (n: number) => `Converted here today (${n})`,
    /**
     * R14-S1 (review-6 BUG-04), folded here by R14-S3: the tray's "below the fold" key -- how many rows the
     * rows' own scroller cuts off, and what the key does (the next cut-off row into view).
     *
     * R14-S18b (UX2 R14-U41, S16 d9df04431): the count WITHOUT the arrow character -- the key draws its own chevron
     * glyph, so the character form ("3 more ↓", `trayMore`) would have put two arrows on one key; that key is retired.
     */
    trayMoreCount: (n: number) => `${n} more`,
    trayMoreLabel: (n: number) => (n === 1 ? 'Show the last file — 1 more below' : `Show the next file — ${n} more below`),
    /**
     * The two per-row accessible names. A `<progress>` and an icon button have no text of their
     * own, so these ARE their names -- and a screen-reader user with eight rows open needs the file
     * in the name or every one of them is "Remove" (review I4).
     */
    trayUploading: (name: string) => `Uploading ${name}`,
    trayRemove: (name: string) => `Remove ${name}`,
    /** R4-JE-01 (review 7): the key on a file whose upload never landed -- pick the file again INTO the same clip. */
    trayReplace: (name: string) => `Replace ${name}`,
    trayReplaceShort: 'Replace',
    thumbPending: 'Reading this file…',
    /** S-32: fits a 200-px row at 12 px. */
    proxyPendingPhoto: 'Preview coming…',
    proxyPendingServer: 'Preparing a preview — this one needs the server',
    /**
     * S-32, corrected by review 6 (BUG-01).
     *
     * It read "No preview — exports fine", and the second half is a promise about the EXPORT that
     * no surface showing this line is in a position to make. The review's three undecodable shapes
     * — a text file renamed `.jpg`, a truncated JPEG, a 0-byte file — all wore it, and the render
     * then hung at 15 %. This line now states the one fact it has (there are no preview pixels)
     * and says nothing about what the export will do; `sourceUnreadableClip` is the line for bytes
     * NOTHING could read, and `previewNoPlayback` the one for a clip whose poster did draw.
     */
    proxyFailed: 'No preview for this file',
    /**
     * Review 6 (BUG-01): the edge could not read these bytes — its `input/info` ladder is spent
     * (`onProbeFailed`) or its preview conversion failed — so the thing that would do the export is
     * the thing that could not read the file. Ranks with `uploadFailedClip` and `fileExpiredClip`,
     * not with a preview problem: a fact and the one way out, in the same voice.
     */
    // R14-S18 (UX2 R14-U34): the row's status line, no name (the name column has it), the gate's remedy --
    // never "add it again" for bytes nothing can decode. Was "We couldn’t read this file — add it again".
    sourceUnreadableClip: 'Can’t be read — remove it',
    /** The same fact on the stage and in the live region: the gate's own sentence (`clipUnreadableGate`). */
    sourceUnreadableStage: clipUnreadableGate,
    /**
     * The pane-level sentence: the gate's, over every unreadable name (R14-U34). It used to say "Add it again
     * to export: text.jpg." -- the one place that still told the user to re-add a text file.
     */
    sourceUnreadableTray: clipsUnreadableGate,
    proxyBadge: 'preview soon',
    /**
     * S-10/S-12 (studio-fix): the header never counts previews. Kept as an empty string so the
     * one call site compiles until A-1 removes the chip; renders nothing.
     */
    proxyHeaderChip: (_n: number) => '',
    /**
     * NOT in §6.8's table, and deliberately not `proxyFailed`.
     *
     * §6.8 has a "Proxy failed" row — "No preview for this file — it will still be in the video."
     * — and a "File expired" row, and no row at all for an upload that never reached the edge.
     * The tray used to borrow the proxy sentence for it, which told the user their file was in a
     * video it cannot possibly be in. This is the missing row, in the same voice as the expired
     * one: a fact and the one thing to do about it, as a clip sentence AND a tray sentence,
     * because §6.8's expired row is that shape and a file that failed its upload can already be on
     * the timeline. It ranks with `expired`, not with a preview problem — P5c's Export block must
     * refuse on it (§6.8's "Export blocked" order: files → re-add → too long).
     */
    uploadFailedClip: 'Upload failed — add this file again',
    uploadFailedTray: (names: string[]) =>
        `These didn’t reach the server. Add them again to export: ${names.join(', ')}.`,
    /** L1 #12: the tile's two words -- see `EXPIRED_COPY`. */
    fileExpiredClip: EXPIRED_COPY.badge,
    fileExpiredTray: EXPIRED_COPY.tray,
    tooManyClips: () => `${STUDIO_LIMITS.maxClips} clips is the limit for now. Remove one to add another.`,

    // --- timeline -------------------------------------------------------------------------
    timelineHeading: 'Timeline',
    timelineHint: 'Drag a clip to reorder · pull an edge to change its length',
    timelineEmptyTail: 'Drop more photos or clips here — they play after this one, 4 s each',
    /** Round 5 (L10): the tile at the end of the track that opens the file picker; `trayAdd` is its full name. */
    timelineAddTile: 'Add',
    // Round 5 (L12): the timeline's zoom keys and the reorder sheet.
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    zoomFit: 'Fit the whole timeline',
    /** Round 6 (H6 UX#6a): the panel hint while the fit cannot bring every tile into the width. */
    zoomFitTooMany: (clips: number) => `${clips} clips are more than fit the width at a readable size — scroll the timeline, or zoom in`,
    reorderKey: 'Reorder',
    reorderTitle: 'Reorder clips',
    reorderHint: 'Drag a clip, or use its arrows · Alt + ↑ / ↓ moves the focused clip',
    /**
     * R14-S3 (review-6 P2-02): the same sheet on a PHONE, where dnd-kit is off (D3) and no keyboard is
     * likely -- only the per-row arrows work there, so only they are offered; no "drag", no "Alt".
     */
    reorderHintTouch: 'Use the arrows to move a clip',
    /** R14-S3 (P2-02): a coarse-pointer tablet -- dnd-kit is on, a keyboard is unlikely: no Alt chord. */
    reorderHintCoarse: 'Drag a clip, or use its arrows',
    reorderUp: (n: number) => `Move clip ${n} earlier`,
    reorderDown: (n: number) => `Move clip ${n} later`,
    reorderRow: (name: string, durationMs: number, index: number, count: number) =>
        `${name}, ${seconds(durationMs)} seconds, clip ${index} of ${count}`,
    visualLane: 'Visual',
    /**
     * A clip tile's accessible name -- §6.8's sentence exactly: "beach-02.jpg, 4.0 seconds, clip 2
     * of 5". It is NOT the visible meta line, which a screen reader would read as "four point zero
     * s"; the two say the same thing to two different readers, which is why both exist and why
     * this one is written here rather than at the tile (review I4).
     */
    clipAnnouncement: (name: string, durationMs: number, index: number, count: number) =>
        `${name}, ${seconds(durationMs)} seconds, clip ${index} of ${count}`,
    /**
     * The trim handles' names. A photo's right handle is not a trim at all -- there is no source to
     * trim from -- so it says what it actually does, which is the same distinction the tile's own
     * handle rule makes (6.2).
     */
    trimStartLabel: (clipNumber: number) => `Trim the start of clip ${clipNumber}`,
    trimEndLabel: (clipNumber: number) => `Trim the end of clip ${clipNumber}`,
    holdHandleLabel: (clipNumber: number) => `Change how long clip ${clipNumber} shows`,
    /**
     * The boundary dot's name. The LABEL comes from `transitionLabels`, never from a second
     * vocabulary: the marker used to say "No transition" where the header said "None" -- two words
     * for one state, on one screen (review I4). The purpose leads and the value follows, so all six
     * types read the same way: "Transition before clip 2: Cross-fade", "…: None".
     */
    transitionMarkerLabel: (label: string, clipNumber: number) =>
        `Transition before clip ${clipNumber}: ${label}`,
    musicLane: 'Music',
    musicMutedNote: 'Clips’ sound · Muted while music plays',
    transitionsNeedTwo: 'Transitions appear once there are two clips',
    musicShorter: (endMs: number) => `The song ends at ${clock(endMs)}. The rest of the video is silent.`,
    // R13-Z1 (owner S6): the fade is OFF by default now, so the sentence names it only when it is on.
    musicLonger: 'Trimmed to the length of your video.',
    musicLongerFading: 'Trimmed to the length of your video, fading out at the end.',
    /** R13-Z1 (owner S5): a song on a timeline with no clips yet -- its own length, and what happens next. */
    musicNoVideo: (spanMs: number) => `Plays for ${clock(spanMs)} — add photos or clips and it is cut to the length of your video.`,
    musicAddedToast: 'Clips muted so your song plays.',
    /**
     * The lane's own three names. `musicUnnamed` is what the slider is called before the file's
     * name is known -- the clip is on screen and draggable either way, so it needs a name either
     * way (review I4).
     */
    musicUnnamed: 'Music',
    musicClipLabel: (name: string, atMs: number) => `${name} starts at ${clock(atMs)}`,
    musicFade: 'Fade out',
    musicRemove: 'Remove the song',
    clipRemovedToast: (name: string) => `Removed ${name}`,
    useForAllToast: (ms: number) => `Every photo now shows for ${seconds(ms)} s`,

    // --- inspector ------------------------------------------------------------------------
    /** The right-hand pane with nothing selected. Desktop only: the sheet never opens empty. */
    inspectorEmpty: 'Select a clip to change it.',
    /** A clip whose file is not staged yet has no name of its own to show. */
    clipFallbackName: (n: number) => `Clip ${n}`,
    kindPhoto: 'Photo',
    /** IT1-W1 (copy 5): a clip is a clip on every surface -- "video" is the OUTPUT (the brief's one vocabulary). */
    kindVideo: 'Clip',
    /**
     * `Main.dc.html:145` writes "Photo · 1440 × 1080 (4:3) · same as the frame";
     * `MobileDuration.dc.html` writes "Photo 2 of 5". Both, in that order: the position is what a
     * user navigating with the keyboard needs first, and the shape and the frame comparison are
     * the two facts that explain why a photo is being cropped or padded at all.
     *
     * Every tail is optional because every one of them can be unknown -- the size until the probe
     * lands, the shape when the photo's aspect matches no preset.
     *
     * QA round 2 Q4: the artboard's "same as the frame" read as "same SIZE as the frame" next to
     * a size the frame was copied from; it now says what it compares -- the shape.
     */
    inspectorSubtitle: (
        kind: string,
        index: number,
        count: number,
        size?: { width: number; height: number } | null,
        shape?: string | null,
        sameAsFrame?: boolean,
    ) => [
        `${kind} ${index} of ${count}`,
        size ? `${size.width} × ${size.height}${shape ? ` (${shape})` : ''}` : '',
        sameAsFrame ? 'same shape as the frame' : '',
    ].filter(Boolean).join(' · '),
    holdLabel: 'Shows for',
    /** The stepper's two accessible names, and the field's own label. */
    holdShorter: 'Shorter',
    holdLonger: 'Longer',
    holdFieldLabel: 'Seconds this photo shows',
    secondsUnit: 'seconds',
    holdPreset: (ms: number) => `${ms / 1000} s`,
    /** Marks the preset that is `DEFAULT_HOLD_MS`; the artboard prints it under the chip. */
    holdPresetDefault: 'default',
    motionStill: 'Still',
    motionSlowZoom: 'Slow zoom',
    motionPan: 'Pan',
    /** `VideoClip.dc.html`: "Trim · 7.0 s · from 0:02.0 to 0:09.0". */
    trimReading: (durationMs: number, startMs: number, endMs: number) =>
        `${seconds(durationMs)} s · from ${clockTenths(startMs)} to ${clockTenths(endMs)}`,
    rotateRight: 'Rotate right',
    flipHorizontal: 'Flip horizontally',
    flipVertical: 'Flip vertically',
    /**
     * The only way to remove a clip with a finger: §6.7 puts the inspector in the sheet on a
     * phone, the timeline's own delete is a KEY (Delete/Backspace), and the artboards draw no
     * delete anywhere. Without this row a phone user cannot take a photo out of their video.
     */
    removeClip: 'Remove',
    /**
     * The stepper's range hint, INTERPOLATED (review I5, and D11's whole point).
     *
     * It used to read "Between 0.5 and 60 seconds." under a field whose `min` is
     * `max(MIN_CLIP_MS, minClipMsFor(effective transition))`. `emptyDocument` ships a 500 ms
     * cross-fade, so the floor in the DEFAULT project is 1.1 s, not 0.5 -- and up to 4.1 s at the
     * 2 000 ms maximum transition. The hint was contradicting the control it sits under, in the
     * one project every user starts from. Both numbers now come from the same two methods the
     * clamp and the `min`/`max` attributes use.
     *
     * §6.8's row is the source of the old literal; the wording change carries into P6.2.
     */
    holdOutOfRange: (minMs: number, maxMs: number) =>
        `Between ${secondsCeil(minMs)} and ${seconds(maxMs)} seconds.`,
    /**
     * Round 7 (UX7#13): said for a beat after a typed number outside the range was CLAMPED on
     * blur -- "90" is taken as 60.0, not left as the "9" typed on the way to it.
     */
    holdClampedTo: (holdMs: number, minMs: number, maxMs: number) =>
        `Set to ${seconds(holdMs)} s — photos show between ${secondsCeil(minMs)} and ${seconds(maxMs)} seconds.`,
    /** ONE term on every screen (round 6, UX#18): the phone used to say "Use for all photos" for the same switch. */
    sameForEvery: 'Same for every photo',
    /**
     * Both artboards draw this as a TOGGLE (`Main.dc.html:156`, `MobileDuration.dc.html:63`), so
     * it is a switch and its "on" is a fact about the project: every photo already shows for the
     * same length. Once that is true the switch has nothing left to do, and a disabled control
     * must say why (6.8) -- this is that reason, folded into its accessible name.
     */
    sameForEveryAlready: (ms: number) => `every photo already shows for ${seconds(ms)} s`,
    otherClipsKeepLength: 'Clips (and animated GIF/WebP) keep their own length',
    fitLabel: 'Fit in frame',
    /**
     * The three fits, as the inspector writes them. The frame popover says "Fill · crop" for the
     * same value (`frameFitFill`): there the consequence is the whole video, here it is one photo.
     *
     * QA round 2 Q4: the owner could not tell which fit was in force or what the words meant, so
     * each chip now carries a plain-words hint under it (the `hint` slot the hold presets already
     * use for "default"), and the explainer says what each one DOES rather than when it matters.
     */
    fitFill: 'Fill',
    fitBlur: 'Blur sides',
    /**
     * CONTAIN_COLOR. "Black sides" until the frame's background became a choice (the Frame control's
     * Background row): the sides are THAT colour, so the label names the kind of fill, not a colour.
     */
    fitBlack: 'Color sides',
    /**
     * Round 4 (C7): ONE sentence under the group, about the fit that is CHOSEN, instead of a
     * paragraph about all three. The three-way comparison lives behind the `?` (`fitTip`).
     */
    fitHint: (fit: 'COVER' | 'CONTAIN_BLUR' | 'CONTAIN_COLOR', subject: 'photo' | 'clip' = 'photo') => (
        fit === 'COVER' ? 'Fills the frame; what doesn’t fit is cropped — drag the preview to choose what shows.'
            : fit === 'CONTAIN_BLUR' ? `The whole ${subject} shows, with a blurred copy filling the sides.`
                : `The whole ${subject} shows, with the background color filling the sides.`),
    /**
     * Instead of `fitHint` when the clip's shape matches the frame's: there is nothing to crop or pad.
     * `subject` is "clip" for a video (round 6, UX#18: the video inspector said "photo").
     */
    fitHintSameShape: (subject: 'photo' | 'clip' = 'photo') =>
        `This ${subject} is the frame’s shape — nothing is cropped or padded.`,
    fitTip: 'Fill crops the photo to the frame. Blur sides and Color sides show the whole photo and fill the gaps. The Frame chip in the header sets the default for every photo you haven’t changed here.',
    motionLabel: 'Motion',
    /**
     * Still is the default (QA round 2, Q5 -- the owner reversed S-30's slow zoom). One sentence
     * per option, behind the `?` (round 4, C7).
     */
    motionExplainer:
        'Still by default — the photo doesn’t move. Slow zoom eases in gently; Pan drifts slowly from left to right.',
    orderLabel: 'Order',
    /** ONE term on every screen (round 6, UX#18): the phone used to say "Move earlier" for the same key. */
    orderEarlier: '‹ Earlier',
    orderLater: 'Later ›',
    duplicate: 'Duplicate',
    split: 'Split',
    trimLabel: 'Trim',
    soundLabel: 'Sound',
    soundKeep: 'Keep',
    soundMute: 'Mute',
    soundNoTrack: 'no sound track',
    volumeLabel: 'Volume',
    rotateLabel: 'Rotate',
    flipLabel: 'Flip',
    transitionClamped: (clampedMs: number, shortestMs: number) =>
        `Cross-fade shortened to ${seconds(clampedMs)} s — your shortest clip is ${seconds(shortestMs)} s.`,
    totalTooLong: (totalMs: number, limitMs: number) =>
        `Your video is ${clock(totalMs)}. The limit is ${Math.round(limitMs / 60000)} minutes — shorten a clip or remove one.`,
    sheetDone: 'Done',
    /**
     * The header popover's footnote. Round 6 (UX#17): it pointed to "Settings", which does not exist;
     * it now says where the ONE-gap control is.
     */
    transitionsAreGlobal: 'Applies between every pair of clips. To change one gap only, use the marker between two clips on the timeline.',

    // --- transition control ---------------------------------------------------------------
    /**
     * The transition control's own two labels. They were written at their call site in the plan,
     * which is the pre-flight's finding 3 -- and `Length` in particular is the word the slider's
     * `<label>` carries, so a QA row cannot address it if it lives in JSX.
     */
    transitionTypeHeading: 'Transition',
    transitionLengthHeading: 'Length',
    /** A range announces a NUMBER unless it is told otherwise; 6.8 wants a time. */
    transitionLengthValue: (ms: number) => `${seconds(ms)} seconds`,
    /** The readout beside the slider (round 6, UX#17): "0.5 s", like the volume row's "100%". */
    transitionLengthShort: (ms: number) => `${seconds(ms)} s`,
    /** The inspector's heading for the boundary that ENTERS the selected clip (P5b.5 ruling 1). */
    transitionIntoClip: 'Transition into this clip',

    // --- preview and transport ------------------------------------------------------------
    /**
     * The canvas is a `role="img"`, so it needs a name -- a STATIC one. The frame it shows changes
     * sixty times a second and the preview never re-renders for a tick, so a name built from the
     * playhead would simply freeze at whatever time React last committed.
     */
    previewLabel: 'Preview',
    /**
     * The playhead, written onto the canvas imperatively beside `data-drawn-ms`. The time itself
     * is ANNOUNCED by the transport's slider, which is the control that owns it; this is what a
     * test (and a curious inspector) reads to see which frame is on screen.
     */
    previewAt: (ms: number) => `Preview at ${(Math.max(0, ms) / 1000).toFixed(1)} seconds`,
    transportPlay: 'Play',
    transportPause: 'Pause',
    transportPlayhead: 'Playhead',
    /** A range announces a NUMBER unless it is told otherwise; 6.8 wants a time. */
    transportPlayheadValue: (positionMs: number, durationMs: number) =>
        `${clockTenths(positionMs)} of ${clockTenths(durationMs)}`,
    transportLoop: (on: boolean) => `Loop ${on ? 'on' : 'off'}`,
    /** Round 4: the Loop switch shows the word; the pill and `transportLoop` (its name) carry the state. */
    loopWord: 'Loop',

    // --- frame control --------------------------------------------------------------------
    frameHeading: 'Video settings',
    /** Owner ruling (review 7, F3): the popover's last section -- the project's default transition. */
    frameBetweenClips: 'Between clips',
    frameShape: 'Shape',
    frameSize: 'Size',
    frameMismatch: "Photos that don’t match the frame",
    /** Round 5 (L4): ONE sentence under the popover's three cards; the rest is behind the inspector's `?`. */
    frameExplainer:
        "The frame starts as the first photo’s own size; any single clip can override the fit in its own settings.",
    /** Round 4 (G1's reposition overlay): shown on hover/focus until the clip has been moved once. */
    reframeHint: 'Drag to reposition',
    /**
     * The overlay is a 2-D slider, so it needs a name and a reading; neither has any text on
     * screen to borrow (review I4). The reading names both axes because the control moves in both,
     * while `aria-valuenow` can only carry one number.
     */
    reframeLabel: (clipName: string) => `Reframe ${clipName}`,
    reframeReading: (acrossPercent: number, downPercent: number) =>
        `showing from ${acrossPercent}% across and ${downPercent}% down`,
    /**
     * `Main.dc.html`'s line under the heading -- the SOURCE of the default, said once so the
     * "Match first photo" chip beneath it needs no explanation of its own. The shape is omitted
     * rather than invented when the photo's aspect matches no preset.
     */
    frameSource: (width: number, height: number, shape?: string | null) =>
        `First photo is ${width} × ${height}${shape ? ` (${shape})` : ''}`,
    /**
     * IT1-W1 (copy 2, P1): the popover's first line when the first photo is LARGER than the frame
     * can be (`FRAME_MAX`): "First photo is 5616 × 3744 · exported at 4096 × 2730, the largest
     * size". Three surfaces used to call the capped size the photo's own ("Original", "· first
     * photo") beside a line saying the photo was 5616 × 3744, and nothing said it was scaled.
     */
    frameSourceCapped: (width: number, height: number, frameWidth: number, frameHeight: number) =>
        `First photo is ${width} × ${height} · exported at ${frameWidth} × ${frameHeight}, the largest size`,
    /** The line under the Size row while the first photo is capped: the rule, with the number it comes from. */
    frameCappedNote: `Photos larger than ${FRAME_MAX} px are scaled to fit.`,
    /** Before any clip has been measured. §6.3 gives the probe 2 s before it says anything. */
    frameSourceReading: 'Reading the first photo…',
    frameShapeMatchFirst: 'Match first photo',
    /** Round 5 (L4): the Shape dropdown's rows -- "Match first photo (4:3)", "16:9 · YouTube, TV". */
    frameShapeMatchFirstWith: (shape: string | null | undefined) =>
        shape ? `Match first photo (${shape})` : 'Match first photo',
    frameShapeOption: (label: string, hint: string | undefined) => (hint ? `${label} · ${hint}` : label),
    frameShapeHints: FRAME_SHAPE_HINTS,
    frameSizeOriginal: 'Original',
    /**
     * R14-S18 (UX2 R14-U33): the Size row's first key while Shape is NOT "Match first photo" -- there
     * "Original" resolves to the largest size AT THE CHOSEN SHAPE (4096 × 2304 for a 3024 × 4032 photo), which
     * is not the photo's own size, so the key stops calling it that. The way back to the photo's size is the
     * Shape select, which `frameSourceShaped` points at.
     */
    frameFullSize: 'Full size',
    /** The lead line in that state: the photo's size, and the control that gives it back. */
    frameSourceShaped: (width: number, height: number) =>
        `First photo is ${width} × ${height} — set Shape to “Match first photo” to use its own size`,
    /** IT1-W1 (copy 2): the Size row's first face when the first photo is capped -- never "Original" for a scaled size. */
    frameSizeLargest: 'Largest',
    /** The header chip's source while the frame follows a CAPPED first photo: "4096 × 2730 · fits the first photo". */
    frameSizeCappedHint: 'fits the first photo',
    frameSize720: '720p',
    frameSize1080: '1080p',
    frameSizeCustom: 'Custom',
    /**
     * The Frame control's Background row (owner ruling 2026-09-25: "the image on your chosen colour").
     * One colour for the whole video: it fills the bars AND every transparent pixel of a PNG/WebP/GIF,
     * which is flattened onto it before anything else happens to it (here and on the server).
     */
    frameBackground: 'Background',
    frameBackgroundGroup: 'Background color',
    frameBackgroundHint: 'Fills the bars and any transparent areas of your images.',
    frameBackgroundCustom: 'Custom background color',
    /** Read with the custom picker while a colour off the swatch list is in force (Sr review N7: the state, not only the look). */
    frameBackgroundCustomInUse: (hex: string) => `In use: ${hex.toUpperCase()}`,
    /** The label row's reading: the swatch's name, or the hex of a custom colour ("#1E90FF"). */
    frameBackgroundValue: (name: string | null, hex: string) => name ?? hex.toUpperCase(),
    frameWidth: 'Frame width',
    frameHeight: 'Frame height',
    /**
     * The frame popover says "Fill · crop" where the inspector says "Fill": at the frame level the
     * consequence is the whole video being cropped, and the artboards write the two differently.
     */
    frameFitFill: 'Fill · crop',
    /**
     * Round 6 (SDE#9): the DEFAULT frame followed a 208 x 244 first photo into a project of
     * 6000 x 4500 ones and the export was 208 x 244. One line, in the frame popover and on the
     * export sheet, naming the size and the row that changes it.
     */
    frameTooSmall: (width: number, height: number) =>
        `Your video will be ${width} × ${height} — pick a Size to export larger.`,

    // --- export sheet ---------------------------------------------------------------------
    exportHeading: 'Export video',
    /**
     * The caption under the Size row, and it is now TRUE (review C1): the row writes `doc.frame`
     * through the frame control's own writer, so picking a size here is picking the frame. It used
     * to say the same thing while nothing mapped the two, which made it the most confident sentence
     * on a screen that was about to export a different size than it showed.
     */
    exportSizeNote: 'The same setting as Frame in the editor — changing it here changes the frame.',
    /**
     * R14-S18 (UX2 R14-U39): the format as the summary's FIFTH row ("Format · MP4 (H.264)"), in the `<dt>`/`<dd>`
     * shape of `exportFrame` / `exportFrameValue`. The loose 12 px line it replaces ("MP4 (H.264) — plays everywhere.
     * More formats later.", `exportFormatLine`) was the one orphan in every layout; the sheet dropped it (S16
     * fae30b2eb) and the key went with it (R14-S18b).
     */
    exportFormat: 'Format',
    // WEB-CORE-07: the codec from `export/output-format.ts`, not a second literal.
    exportFormatValue: `MP4 (${STUDIO_VIDEO_CODEC_LABEL})`,
    /**
     * S1-06 (review 7): on a page named for another container (JPEG→MOV, PNG→WEBM, …) the Studio says, before the
     * user builds anything and again beside the Format row, that its video is MP4 -- never a MOV promised by the page
     * and an MP4 delivered in silence. Quick convert on the same page does make that container.
     */
    pageFormatNote: (ext: string) => `The Studio makes MP4 videos, not ${ext}. For a ${ext} file, use Quick convert on this page.`,
    exportFileName: 'File name',
    /**
     * X1-03 (review 7): said UNDER the name field, live, whenever the name that will be saved differs from the one typed
     * -- too long for the server's 200-byte limit, or carrying characters a file name cannot (`/`, `:`, a bidi control).
     * Before, a 161-character name was cut to 80 in silence.
     */
    exportNameNote: (saved: string) => `It will be saved as “${saved}.mp4” — file names are limited in length and can’t contain / \\ : * ? " < > |.`,
    exportNameTooLong: (saved: string) => `Too long for a file name — it will be saved as “${saved}.mp4”.`,
    exportLength: 'Length',
    exportFrame: 'Frame',
    exportClips: 'Clips',
    /** IT1-W1 (copy 6): ONE name for the way back, the /downloads row's -- "Back to Studio" on every screen. */
    exportBack: 'Back to Studio',
    exportGo: 'Export',
    exportBlockedNoClips: 'Add at least one photo or clip first.',
    exportBlockedExpired: 'Re-add the missing files first.',
    /**
     * THE REOPENED PROJECT — a different fact from the expired one, and it must not borrow its words.
     *
     * A project restored from `?sid=` or from `STUDIO-LATEST-<slug>` has its timeline, its options
     * and its previews, because `StoredClipRef` addresses the bytes on the edge. What it does NOT
     * have is a live `UploadInProgressFile` for any input, and that is the only thing
     * `createMergeFileUpload` can build a submission out of (`utils/file-upoad.utils.ts:100-148`),
     * so Export is blocked until the files are added again — until P6.3 widens the record
     * (final review §1(5)).
     *
     * Saying "Files are kept a few hours. Add these again to export" there is FALSE: the files are
     * on the edge, addressable, and were painting on screen a second earlier. This row states the
     * fact that is true — the project was reopened — and asks for the same action, which is why it
     * is a separate row in §6.8's voice rather than a reuse of the expired one.
     */
    exportBlockedReopened: 'Add the files again to export.',
    exportReopenedFiles: (names: string[]) =>
        `This project was reopened, so its files have to be added again to export: ${names.join(', ')}.`,
    /** `Export.dc.html` puts the extension beside the field, never inside it. */
    exportExtension: '.mp4',
    /** `Export.dc.html`: "Frame · 1440 × 1080"; the note beside it is `exportSizeNote`. */
    exportFrameValue: (width: number, height: number) => `${width} × ${height}`,
    /**
     * BUG-16 → R14-Q13 (R14-S8's ruling): the sheet quotes NO size line any more. The point estimate
     * `exportSizeValue` and its "rough guide" caveat went in SR1 #12; the fitted range `exportSizeRange`
     * and its "Estimated size" label (`exportEstimatedSize`) went when QA measured the range 4× under
     * the delivered file (R14-Q13) and nothing outside the spec read them (R14-S9). Owner ruling
     * 2026-09-27: the time line ("Estimated time" / `exportTimeValue`) is gone the same way -- the
     * live progress bar on the render step is enough.
     */
    /**
     * The sentence a refusal falls back to.
     *
     * Every `Violation.code` §6.8 wrote a row for renders that row, and the rest render the
     * server's own `userMessage` (§1 line 66: "the message stays for display"). This is for the
     * third case only — a refusal that arrived with no sentence at all — because the one thing
     * that must never reach the screen is the machine code.
     */
    exportRefused: 'This project can’t be exported as it is. Change it and export again.',
    /**
     * The `hold_too_long` refusal, as a sentence with a subject.
     *
     * NOT `holdOutOfRange`. That row is §6.8's "Hold out of range" state, whose behaviour column is
     * "stepper clamps" — beside the field it annotates, "Between 0.5 and 60 seconds." has the field
     * for a subject; as the entire body of a red alert after pressing Export it is a fragment about
     * nothing. It also bakes 0.5 and 60 into the string, so a change to `STUDIO_LIMITS.maxHoldMs`
     * would silently make it lie, which is the very thing D11 put the limits in this table for.
     */
    exportRefusedHoldTooLong: (maxHoldMs: number) =>
        `One of your photos shows for longer than ${Math.round(maxHoldMs / 1000)} seconds. Shorten it and export again.`,
    /**
     * The 409 row. `beginScoped` compares the stored `requestHash` before anything else and a
     * mismatch is `schedule_key_conflict` (§3.3) — this project was already submitted at this
     * revision with different settings, so what is on screen is not what the server holds.
     * Re-opening re-reads it; retrying the same submission would 409 again.
     */
    /*
     * S3-02 (review 7): the old sentence ("exported from somewhere else") was false for the commonest case -- the SAME
     * browser exporting an unchanged project again. It says what is true: this exact project was already exported;
     * the video is on Downloads; Export again makes a new copy (the next submission mints a new revision).
     */
    /*
     * JE-05 (acceptance R2): said only after the automatic re-submissions under a new revision failed too. It claims no
     * result: the old video on Downloads is NOT this project as it stands, and the user must not believe their edit went out.
     */
    exportConflict:
        'We couldn’t start this export: another export of this project is using the same version. Your edits are still here — press Export again.',
    exportConflictAction: 'Export again',
    exportConflictDownloads: 'Go to Downloads',
    /** The button that takes the user to the clip a refusal or a render failure named. */
    showClip: (index: number) => `Show clip ${index}`,

    // --- render step ----------------------------------------------------------------------
    renderHeading: 'Making your video…',
    renderStarting: 'Starting…',
    renderStages: ['Uploading originals', 'Preparing your photos and clips', 'Adding music and transitions', 'Finishing MP4'],
    /** `Export.dc.html` writes a finished stage as "Uploading originals — done". */
    renderStageDone: (label: string) => `${label} — done`,
    renderRemaining: (secondsLeft: number) => `about ${secondsLeft} seconds left`,
    /**
     * IT1-W1 (copy 1, P1): no made-up number. "A 3-minute video takes about a minute" stood over an
     * export that took a minute for 24 s of video; the render panel's own estimate (from this
     * project's passes) is the number, this line is the promise that holds either way.
     */
    renderWaiting: 'Keep this tab open, or continue in the background — your video is made on our servers either way.',
    renderStalled30: 'Still working. Large photos take longer.',
    renderStalled180:
        "This is taking longer than usual. Your video is still on the server — leaving this page won’t lose it.",
    /**
     * Round 7 (UX7#9): "Stop waiting" read as Cancel and was a trip to /downloads. The key says
     * where it goes; the sentence under it says the render goes on and cannot be cancelled from
     * here (D10: no server cancel exists once the job is accepted). The real Cancel is drawn only
     * while the schedule ladder is still retrying -- nothing has been accepted yet.
     */
    stopWaiting: 'Continue in the background',
    stopWaitingDone: "Your video is still being made — it can’t be cancelled from here. It will be on Downloads when it’s done.",
    renderBackToEditor: 'Back to Studio',
    renderBackToEditorNote: 'Your video is still being made; it will be on Downloads when it’s done.',
    /**
     * The name of the ad landmark, on `AnchorMotiveComponent`'s precedent
     * (`anchor-motive.component.tsx:159` — `role="complementary" aria-label="Advertisement"`), and
     * the caption `Export.dc.html` draws above the 300 × 250. A rectangle with no accessible name
     * is an unlabelled landmark a screen-reader user lands in with no idea what it is.
     */
    adLabel: 'Advertisement',
    /** Round 8 (K2, O11): the phone's sticky bottom bar's 32 px close key. Closed stays closed for the session. */
    adClose: 'Close ad',
    /**
     * IT1-W1 (saas #3): the ONE plan hook, at the refusal moments -- the export sheet's limit view
     * (qa #3), the header's blocked-Export reason for the caps. `/product/backer_monthly` is the
     * route that answers 200 on QA (`/pricing`, `/plans` and `/portal/plans` are 404). The three
     * `adFree*` rows ("Go ad-free from $4.99/month … made without waiting in line") had zero call
     * sites and promised a paid lane nothing in ironman backs; they are gone.
     */
    planCta: 'See plans',
    planHref: '/product/backer_monthly',
    planHint: 'Need more? See the plans.',
    /** Deprecated alias of `planCta`, kept only because `studio-ad.component.spec.tsx:236` reads the key (NEEDS W2: drop that line, then this). */
    adFreeCta: 'See plans',
    /** The limit view's usage line, when the answer carries both numbers. */
    limitUsage: (used: number, limit: number) => `${used.toLocaleString()} of ${limit.toLocaleString()} used`,

    // --- failures and service states ------------------------------------------------------
    renderFailedClip: (index: number, name: string) => `Couldn’t make the video: clip ${index} (${name})`,
    renderFailedClipBody:
        "The file couldn’t be read. Remove it and export again, or try uploading it once more.",
    /**
     * Round 4 (G5): `trim_out_of_range` on the SONG -- from the pre-flight, from a 400 or from the
     * worker -- with the song's own name. The server's sentence ("The music is trimmed past the end
     * of its file") is true and unhelpful: it names nothing and offers nothing, and "Fit to video"
     * is the inspector's own button.
     */
    musicTrimPastEnd: (name: string) =>
        `${name || 'The song'} is trimmed past its end. Shorten it, or use Fit to video.`,
    /**
     * A clip refused by a rule, reached from the CARD rather than from a 400 — the §6.8 row for a
     * structured `compositionFailure` (ironman b4bc7263 puts the code and the clip index on it).
     *
     * Separate from `renderFailedClip`, which says "the file couldn't be read" and offers a
     * re-upload: nothing is wrong with the file here, the trim is. The sentence is the browser's
     * own for the same reason `musicTrimPastEnd` is — the server's "Clip 1 is trimmed past the end
     * of its video." is true and names no fix.
     */
    renderRefusedClipTrim: (index: number, name: string) =>
        `Clip ${index}${name ? ` (${name})` : ''} is trimmed past the end of its video.`,
    renderRefusedClipBody: 'Drag its end handle back inside the video, then export again.',
    /** A clip refused by a rule this client has no row for: name the clip, offer the way back. */
    renderRefusedClip: (index: number, name: string) =>
        `Couldn’t make the video: clip ${index}${name ? ` (${name})` : ''} was refused.`,
    /** S3-05 (review 7): rebuilding a project this browser lost, from its workflow record. */
    rebuildingProject: 'Getting your project back from your last export…',
    rebuiltProject: 'Your project is back, rebuilt from your last export.',
    rebuildFilesGone: 'This project’s files are no longer on our servers — they’re kept for 2.5 hours after upload.',
    rebuildFailed: 'This browser no longer has that project, and it couldn’t be rebuilt from the export.',
    /** S3-07: a reloaded render panel before the job's next frame. */
    renderReconnecting: 'Reconnecting to your video…',
    renderFailed: 'Export failed.',
    /*
     * S3-04 (review 7): no guess. "Remove the newest clip" named a clip the server never blamed (a truncated MP4 that
     * was NOT the newest). When the render names a clip, `renderFailedClip` says which; this line is for when it does not.
     */
    renderFailedBody: 'Nothing was lost — your timeline is still here. Try again; if it fails again, one of the files may be damaged.',
    retry: 'Retry',
    enforcement: 'Your timeline is saved — come back and export again later.',
    studioUnavailable:
        'The Studio is off right now. Your timeline is saved in this browser — try again in a few minutes.',
    offline: "You’re offline. Editing still works — you can’t add files or export until you’re back.",
    twoTabs: 'This project was updated in another tab.',

    // --- restore --------------------------------------------------------------------------
    restoredRendering: 'Welcome back — your video kept going while you were away.',
    restoredEditor: 'Picked up where you left off.',
    restoredPartial: 'Picked up where you left off — some files expired and need adding again.',

    // --- Studio fix pass (STUDIO_FIX_PROMPT.md §6 + wave additions) -----------------------
    // Every row below was declared at its call site while this file was pinned for the wave, and
    // moved here verbatim afterwards. Grouped by the S-nn (or R-n) that introduced it.

    // S-20: the announcer's line for a reorder.
    movedTo: (name: string, position: number) => `Moved ${name} to position ${position}`,

    // S-21: the inspector's "?" and the scoped keys it documents (`TRACK_KEYS` in `keyboard.ts`).
    keysLabel: 'Keyboard shortcuts',
    // Round 6 (UX#22): the Studio's own `?` sheet (`views/shortcuts-sheet.component.tsx`).
    shortcutsHeading: 'Keyboard shortcuts',
    /** The header's `?` key: its name and tooltip. */
    shortcutsLabel: 'Keyboard shortcuts (?)',
    shortcutsEverywhere: 'Anywhere in the Studio',
    shortcutsOnClip: 'With a clip selected on the timeline',
    shortcutSeekSecond: 'Move the playhead one second',
    shortcutsOpen: 'Open this list',
    /** IT1 (parent): the one "more below" cue -- inspector pane, phone clip sheet. */
    moreBelow: 'More below',
    /**
     * R14-S21 → S20b (UX3 R14-U43, Sr review 4 #4): the timeline cue's VISIBLE word -- `moreBelow` ("More below", its
     * accessible name and title) is ≈ 70 px at 13/600 and the gutter slot it stands in is 64; the chevron glyph beside
     * it carries "below". One word on the pill, the whole phrase in the name.
     */
    moreShort: 'More',
    /** R13-Z2 (S3): the stage's corner label -- the stage IS the output frame. */
    outputCaption: (width: number, height: number) => `Output · ${width} × ${height}`,
    /**
     * R14-S18 (QA R14-Q21): the same label while the FIRST photo's shape is not yet known -- the add-time decode
     * answers 100–230 ms after the pick, and the label used to write the 1280 × 720 fallback for that long (one
     * or two paints of a 16:9 box that then snapped to the photo). Same words as `frameChipReading`.
     */
    outputCaptionPending: 'Output · reading the photo…',
    /** Owner report (review 7): the first clip is a video whose shown size is being read. */
    outputCaptionPendingVideo: 'Output · reading the video…',
    /** IT1-W2 (ux 3): the Files pane lists placed files too; the row's state line. */
    trayOnTimeline: 'On the timeline',
    trayOnTimelineFor: (ms: number) => `On the timeline · ${clockTenths(ms)}`,
    trackKeys: TRACK_KEYS_TABLE,

    // S-22: the two edges' accessible names; the per-clip sentences stay on `title` as the hint.
    handleStartLabel: 'Start edge',
    handleEndLabel: 'End edge',

    // S-35: the announcer's undo/redo sentences.
    historyUndid: (label: string) => `Undid: ${label}`,
    historyRedid: (label: string) => `Redid: ${label}`,

    // S-40 / round 6 (UX#9): the export sheet's two xconfig cards wear the FRAME POPOVER's words.
    /** The `VIDEO_RESOLUTION` card's label -- the same word as the popover's Size row. */
    exportSizeLabel: 'Size',
    /** The `VIDEO_COMPRESSION` card's label: "File Compression · Very High" read as the worst quality. */
    exportQualityLabel: 'Quality',
    /**
     * The compression group's OTHER modes (the Quality select's tail), by their xconfig ids
     * (`compression-group.component.tsx`; the ids stay on the wire, only the faces change). The
     * tree spells them "Constant Quality / Constraint Quality" -- round 7 (UX7#10, H6#9) gives them
     * plain words. The presets ("Highest … Lowest") keep the tree's names.
     */
    exportQualityModes: Object.freeze({
        FILE_SIZE_PERCENTAGE_WITH_AUTO_SCALE: 'Target file size (%)',
        FILE_SIZE_EXACT_WITH_AUTO_SCALE: 'Target file size (MB)',
        VIDEO_CONSTANT_BITRATE_WITH_AUTO_SCALE: 'Constant bitrate',
        VIDEO_VARIABLE_BITRATE_WITH_AUTO_SCALE: 'Variable bitrate',
        VIDEO_CONSTANT_QUALITY: 'Constant quality (CRF)',
        VIDEO_CONSTRAINT_QUALITY: 'Constrained quality (CRF with a bitrate cap)',
    }) as Readonly<Record<string, string>>,
    /**
     * ONE formatter for the video's length wherever it is a TOTAL (round 6, QA#19): the timeline's
     * chip said "20.5 s" while the export sheet's Length said "0:21" and the transport "0:20.5".
     * `clockTenths`, the transport's own stamp, so the three agree to the tenth.
     */
    totalLength: (ms: number) => clockTenths(ms),
    // S-40/S-41: `Export.png`'s "5 photos · 1 video · 1 song", derived from the document.
    exportClipMix: (photos: number, videos: number, songs: number) =>
        [
            photos > 0 ? `${photos} photo${photos === 1 ? '' : 's'}` : '',
            videos > 0 ? `${videos} clip${videos === 1 ? '' : 's'}` : '',
            songs > 0 ? `${songs} song${songs === 1 ? '' : 's'}` : '',
        ].filter(Boolean).join(' · '),

    // S-42: the return from /downloads -- the toast and its one action.
    restoreRenderDone: 'Your video is on Downloads',
    restoreRenderGone: 'That video finished or expired',
    restoreOpenDownloads: 'Open Downloads',

    // S-43: the `/downloads` card's line -- "Converted MP4 to MP4" → "Studio video · 4 photos".
    cardStudioVideo: 'Studio video',
    cardPhotos: (n: number) => `${n} photo${n === 1 ? '' : 's'}`,
    cardVideos: (n: number) => `${n} clip${n === 1 ? '' : 's'}`,
    cardSongs: (n: number) => `${n} song${n === 1 ? '' : 's'}`,

    // R1/R5 (shell): the exit button, the phone header's back arrow, and the drawer button's name.
    /**
     * Round 4 (owner C7): "← Quick convert" read as the thing to press to convert the photos into
     * a video. The button LEAVES the Studio, so it says where it goes. `CONVERT_TAB_LABEL` still
     * names the tab itself.
     */
    exitLabel: '\u2190 Back to converter',
    /** The phone header's back arrow is the same button with its text as the name only. */
    exitGlyph: '\u2190',
    /**
     * Round 8 (K2, O8; owner: "it's not clear what this arrow is"): the phone's back key carries a
     * WORD. `exitLabel` stays its accessible name (where it goes); this is what is drawn.
     */
    exitLabelShort: '\u2190 Back',
    /**
     * Round 8 (K2, O8/S4): the phone header's `studio-fit-screen` key. "Fit to screen" pins the
     * editor to the viewport (the shell's fixed-inset fallback) so a swipe on the timeline can never
     * scroll the page; the same key lets it go.
     */
    fitScreenEnter: 'Fit to screen',
    /**
     * Round 14 (S, R13 QA M6): no longer drawn -- the `⋯` item keeps ONE name in both states and
     * says its state through `aria-checked`, as the tablet key does through `aria-pressed` (N11).
     * Kept for the e2e fixtures' copy table.
     */
    fitScreenExit: 'Exit fit to screen',
    /** The drawer button's accessible name with its badge, "Files, 2 not on the timeline". */
    filesToggleLabel: (n: number) => `Files, ${n} not on the timeline`,

    // R5 (clip sheet): the phone's clip summary card and sheet chrome.
    sheetEdit: 'Edit',
    sheetEditGlyph: '\u203a',
    sheetClose: 'Close',
    /** Round 8 (K2, O9): the clip sheet's drag handle -- its accessible name says what a drag does. */
    sheetHandle: 'Drag to resize the panel · drag down to close',
    sheetDeleteClip: 'Remove this clip',
    /** "Photo 2 of 5" -- the sheet's subtitle (MobileDuration.png); `index` is zero-based. */
    sheetSummaryKind: (kind: 'photo' | 'video', index: number, count: number) =>
        `${kind === 'video' ? 'Clip' : 'Photo'} ${index + 1} of ${count}`,
    /** IT2-Y1b (copy 4): the tile's floored tenths -- the card said "15.0 s" over a sheet that said 14.9. */
    sheetSummarySeconds: (ms: number) => `${seconds(ms)} s`,
    sheetEditAria: (name: string) => `Edit ${name}`,

    // R2 (files tray): `mocks/EmptyState.png`'s tray sentence.
    trayEmptyExplainer: 'Files you add land on the timeline straight away — the editor works with small '
        + 'previews, your export uses the full-size originals.',

    // Q7/Q8 (timeline lane): the draggable playhead knob is a slider; this is its accessible name.
    playheadLabel: 'Position',

    // R3-F1 (chrome): the phone header and the phone's summary row (Mobile.png).
    /** Mobile.png's Export button: the same intent, one word. */
    exportButtonShort: 'Export',
    /** Mobile.png's Frame chip: the size alone, no source -- the row has no room for "· first photo". */
    frameChipShort: (w: number, h: number) => `${w} × ${h}`,
    /**
     * Round 13 (S, R12 QA N12): the PHONE chip carries its noun -- beside "No transition" a bare
     * "4096 × 2730" read as a size, not as the frame. The glyph goes on the phone (studio.module.scss
     * `.headerPhoneChips`) so the word has its room at 360.
     */
    frameChipPhone: (w: number, h: number) => `Video settings · ${w} × ${h}`,
    /**
     * R14-S21 → S20b (QA3 R14-Q27, Sr review 4 #4): the ONE-ROW phone header's Frame chip at 390 × 508 -- `frameChipShort`'s
     * "3024 × 4032" measured 104 px where the row budgeted 82, so the row overflowed 10 px with a 4-digit frame. The tight
     * spelling drops the spaces around the sign (`3024×4032`); the chip's accessible name stays the long sentence, and the
     * chip says `data-tight="true"`. Outside U49's grammar: a size-only form, there is no source to say.
     */
    frameChipShortTight: (w: number, h: number) => `${w}×${h}`,
    /**
     * The tablet header's Between-clips chip: "Cross-fade 0.5 s" stands alone, but a bare "None"
     * beside a bare "616 × 768" said nothing (round 6, UX#18) -- that state keeps its prefix.
     */
    transitionChipShort: (label: string, ms: number | null) =>
        (ms === null ? `Between clips · ${label}` : `${label} ${seconds(ms)} s`),
    /**
     * Round 8 (K2, O8): the PHONE header's Between-clips chip shares row 2 with the Frame chip and
     * Export, where "Between clips · None" clipped. R14-S3 (review-6 BUG-17): the phone said "No
     * transition" for the state the desktop calls "Between clips · None" -- two sentences for one
     * state. Now every width uses the desktop sentence's own words: the phone shows its FIRST words
     * for None ("Between clips"; measured on :3002 at 390 × 844 with "Frame 1600 × 1200" and Export in
     * the row, the chip has 119 px and "Between clips · None" needs ~153; 360 leaves 89, 430 leaves
     * 159) and its LAST words for a timed one ("Cross-fade 0.5 s", the tablet's short form). The
     * accessible name stays the full sentence (`transitionChipStatic`).
     */
    transitionChipPhone: (label: string, ms: number | null) =>
        (ms === null ? 'Between clips' : `${label} ${seconds(ms)} s`),
    /**
     * The one-line hint in the phone's summary row while no clip is selected (brief §3 F1). R14-S3
     * (review-6 BUG-27): the real gesture -- a tap selects, the second tap on the selected clip opens
     * its editor (R14-S2) -- so the hint says both; one line at 360 (12 px, ~265 px of 328).
     */
    summaryHint: 'Tap a clip to select it · tap again to edit',
    /** The transport's sound toggle (`studio-sound-toggle`): its accessible name in each state. */
    soundToggle: (on: boolean) => (on ? 'Sound on' : 'Sound off'),
    /** The ADVERTISEMENT label over the in-editor rectangle (Main.png) is `adLabel`, upper-cased by CSS. */

    // R14-S2 (review 6): folded from `studio-tab.component.tsx` `R14_COPY` and `clip.component.tsx` `R14_CLIP_COPY`
    // by R14-S3; the aliases in those files keep their specs' names.
    /**
     * BUG-13 / BUG-14: the header's Export reason, the stage banner and the refusal the announcer reads while a
     * render runs -- the project is read-only until the render settles; Export re-opens the progress panel.
     *
     * R14-S9 (Sr review batch 1, #14): every consumer of this row is IN THE EDITOR -- the banner after "Back to
     * Studio", the header's reason, the refused add/remove/undo (`studio-tab.component.tsx` `refuseWhileRendering`,
     * `reopenRenderPanel`, `onBackFromExport`, `exportBlockedReason`, the stage banner). "wait or continue in the
     * background" offered a choice the user had already made: the editor IS the background, and the panel's own
     * "Continue in the background" key (`stopWaiting`) lives on the panel, where this sentence is never shown. So
     * ONE sentence, the true one, for every consumer: what is locked and until when; the banner's key names the
     * way back to the panel. No two-way variant for the header's reason -- it is read in the same place.
     */
    renderLock: 'Your video is being made — the timeline is locked until it’s done.',
    /** BUG-13: the banner's key back to the progress panel. */
    renderLockShow: 'Show progress',
    /**
     * R14-S18 (UX2 R14-U32): the phone's ONE-ROW stage notice -- the box under U30 is 183 × 244 at most, and the
     * full sentence took 91 px under a 50 × 62 box and was repeated in the summary row 20 px lower. The row reads
     * this, with `renderLockShow` right-aligned; the summary row keeps `renderLock` as the reason at rest.
     */
    renderLockShort: 'Making your video — timeline locked.',
    /**
     * R14-S20 (UX3 R14-U46): the ONE-ROW notice's key on the phone. UX3 measured `renderLockShow` at 118 px in the
     * 364 px row, leaving the 13/600 sentence (≈ 234 px) 222 px and a second line ("…timeline / locked."). "Progress"
     * (≈ 78 px) gives it 262 px and one line; the panel it opens IS the progress panel, so "Show" added nothing at 44 px.
     * The full notice (inside the box, the desktop) and the toast keep `renderLockShow`.
     */
    renderLockShowShort: 'Progress',
    /**
     * R2-03 (the tail of BUG-01): the Export gate over a source nothing could read, naming it.
     * R14-S18 (R14-U34): the one unreadable sentence (`clipUnreadableGate`) -- "remove it or add another file"
     * offered a second remedy the row contradicted.
     */
    exportBlockedUnreadable: clipsUnreadableGate,
    /** R14-U34: the family's one-name form, by name, for the stage and the live region. */
    clipUnreadableGate,
    /** BUG-03 (b): the pick a reload cut short is going up again on its own. */
    uploadsResumed: (names: readonly string[]): string => {
        const shown = names.slice(0, 2).join(', ') + (names.length > 2 ? ` and ${names.length - 2} more` : '');
        return `Your files were still uploading — picking up where they left off${shown ? ` (${shown})` : ''}.`;
    },
    /** R2-03: the tile's two-word badge for `sourceUnreadableClip`, cut to a 96 px tile (`EXPIRED_COPY.badge`'s shape). */
    clipUnreadableBadge: 'Can’t read file',
    /** BUG-03: the tile's badge for `INTERRUPTION_COPY.uploadsLost`, cut to the tile. */
    clipInterruptedBadge: 'Upload cut short',
    /**
     * R14-S8 (QA R14-Q9), folded here by R14-S9: the tray ROW's sentence for a slot a reload cut short --
     * no file name, the row's name column already says which file. The pane's sentence
     * (`INTERRUPTION_COPY.uploadsLost`, names in brackets) used to be reused per row and said the name twice
     * ("1.JPG Your files were still uploading — add them again (1.JPG)."). `TRAY_R14_COPY.uploadCutShort` in
     * `files-tray.component.tsx` is the alias its spec addresses.
     */
    uploadCutShort: 'Your upload was cut short — add it again.',
    /**
     * R14-S18 (QA R14-Q20): a second tab on the same project while the FIRST tab still uploads. The record's
     * empty ids used to read as an upload a reload cut short ("cut short", "expired", Export `interrupted`) for
     * files 5 s into a healthy upload next door. The tab decides this state when the stash holds rows another
     * writer wrote that are not yet stale (`stashRowIsTakable` false -- R14-S7's rule): the row and the tile
     * badge say where the bytes are, the gate says what to do, the live region reads the gate once. It
     * converges on the other tab's next record write that carries the ids.
     */
    uploadingElsewhereRow: 'Uploading in another tab',
    /** The tile's badge: the same words (they fit a 96 px tile as `clipInterruptedBadge` does). */
    uploadingElsewhereBadge: 'Uploading in another tab',
    /** The Export gate, the header's reason and the announcer. */
    uploadingElsewhereGate: 'Your files are uploading in another tab — wait, or add them here.',
    // R14-S6 (R14-V7, R14-V3b): folded from `studio-tab.component.tsx` (R14-S6's local table) by R14-S10 -- S9's held fold.
    /** "Fix it" on a trim past the end of its file (R14-V7): what was done, and to which clip. */
    trimFixed: (index: number, name: string): string =>
        (name ? `Clip ${index} (${name}) now ends where its file ends.` : `Clip ${index} now ends where its file ends.`),
    /** The history label of that edit (the undo key's tooltip). */
    trimFixedLabel: 'Trim to the end of the file',
    /** R14-V3b: the Export gate while a still's own bytes are being checked (a few hundred ms). */
    exportBlockedChecking: (names: readonly string[]): string => (names.length === 1
        ? `Still checking ${names[0]} — a moment.`
        : `Still checking ${names.length} files — a moment.`),
    /**
     * R14-S10 (R14-Q4, R14-S6's NEEDS): the failure sheet's key for a clip the server could not read
     * (`CLIP_DECODE`). Retry would start the same doomed render, so the way out is the clip's removal --
     * the editor's `deleteClip`, with its own "Removed <name>" toast and undo. `index` is 1-based, as
     * `showClip`'s is.
     */
    exportFailedRemoveClip: (index: number) => `Remove clip ${index}`,

    // R3 (timeline panel, music lane, preview audio): consolidated from the slices' local consts.
    /** The timeline panel's sound chip: what the video clips' own sound does right now. */
    soundChipOn: 'Clips’ sound · On',
    soundChipMuted: 'Clips’ sound · Muted',
    /** S2-05 (review 7): the status chip's hint -- where the clips' sound is actually changed. */
    soundChipHint: 'To mute or unmute a clip, use its speaker key or the Sound switch in the clip panel.',
    /** The phone's Music pane (Mobile.png's Music tab): add / replace, and the song's placement line. */
    musicAddPane: 'Add music from your device',
    musicReplace: 'Replace the song',
    /**
     * Round 6 (UX#18): the second number is the TRIM -- how much of the song plays -- so it says so;
     * "0:32 long" beside a 3:40 file read as a wrong length. The file's own length is
     * `musicPanelMeta` ("3:40 long") in the panel's title row.
     *
     * R14-S9 (QA R14-Q17, via R14-S7): TENTHS, like the two lines under it. `clock` ROUNDS to whole seconds, so a
     * 9 500 ms trim read "plays 0:10" in the song row while the same pane said "Plays 0:00.0 – 0:09.5"
     * (`musicPlaying`, `clockTenths`) and "TRIM 9.5 s" (`secondsTenths`) -- one length, two roundings. Both numbers
     * here are floored tenths now (IT1-W1 copy 4: one formatter for every running time).
     */
    musicPaneMeta: (atMs: number, playsMs: number) => `starts at ${clockTenths(atMs)} · plays ${clockTenths(playsMs)}`,
    /** The empty music lane's ghost row and its hint (brief §3 F2). */
    musicAdd: 'Add music',
    musicAddHint: 'optional · use music you have the rights to',
    /**
     * Round 9 (L1 #3): a song that sits in Files unplaced while the lane has none -- picked through
     * the general control, or taken off the lane. The empty row and the phone's Music pane say
     * where it IS instead of "Add music" (R8 QA #3: the song sat as "Ready" for 120 s, no word).
     * Round 10 (M1, R9 QA N11): on a phone the "+" is on another TAB, so the sentence names the place.
     */
    musicWaiting: (name: string, compact = false) => (compact
        ? `${name} is in Files — open Files and tap + to add it`
        : `${name} is in Files — tap + to add it`),
    /** Round 10 (M1, R9 QA N11): the phone Music pane's own key that places the waiting song. */
    musicPlaceWaiting: 'Add it to the video',
    /** The note before a song that starts later than 0:00: "from 0:04 · drag to move". */
    musicFrom: (atMs: number) => `from ${clock(atMs)} · drag to move`,
    /** Said once per mount when the browser refuses `play()` on the music element (autoplay policy). */
    musicBlockedNotice: 'The browser blocked the music from starting. Press Play again to hear it.',

    // R4-G3 (round 4, C1-C7): the compact inspector, the video and music panels, the pane toggles,
    // fullscreen, the leaderboards and the copy pass.
    /** The title row's 28 px `?`: one popover with a sentence per group and the keyboard shortcuts. */
    hintsLabel: 'Tips and shortcuts',
    hintsHeading: 'Tips',
    holdTip: 'Pick a length or type your own. Same for every photo applies it to all of them, now and later.',
    transitionTip: 'How this clip appears after the one before it. The Between-clips chip in the header sets it for every clip at once.',
    trimTip: 'Drag the handles or type a time as m:ss.t. The clip plays at its own speed.',
    musicTip: 'Move the song with Starts at, cut it with Trim, or press Fit to video so it ends with your video.',
    /** The custom stepper's accessible name (the group reads "Shows for"). */
    holdCustom: 'Custom length',
    /** The video panel's meta line: "0:12.3 of 0:48.0 · 1920 × 1080". */
    /**
     * Round 13 (S, R12 QA N4): "of 0:48.0" only when the clip is TRIMMED -- an untrimmed "0:14.9 of 0:14.9"
     * said nothing and pushed the dimensions (the part the owner asked to see) off the one-line head at
     * 1440-768. Shortest form first: the length, then the size.
     */
    videoSubtitle: (usedMs: number, sourceMs: number | null, size?: { width: number; height: number } | null) => [
        sourceMs && sourceMs > 0 && Math.abs(sourceMs - usedMs) > 50 ? `${clockTenths(usedMs)} of ${clockTenths(sourceMs)}` : clockTenths(usedMs),
        size ? `${size.width} × ${size.height}` : '',
    ].filter(Boolean).join(' · '),
    trimStart: 'Start',
    trimEnd: 'End',
    /** Round 5 (L2): "Whole clip" -- in the TRIM card, beside Start and End, the three fit one 8 px row
     *  in a 340 px column; "Use the whole clip" was 20 px too wide and overflowed the card. */
    trimWhole: 'Whole clip',
    trimHandleStart: 'Trim start',
    trimHandleEnd: 'Trim end',
    /** The time fields take m:ss.t; this is the reading and the placeholder. */
    timeFieldHint: 'm:ss.t',
    soundKeepSwitch: 'Keep the clip’s sound',
    soundNoTrackLine: 'This clip has no sound track.',
    /**
     * Round 10 (M1, R9 QA N2): the Sound group's state word and line while the PREVIEW cannot play
     * this clip's sound (`ClipRuntime.previewSound === 'export-only'`). The switch keeps its name --
     * it still decides the EXPORT -- and the group says what the ears will not hear.
     */
    soundExportOnlyState: 'In export only',
    soundExportOnlyLine: 'This browser can’t play this clip’s sound — your export includes it.',
    /** The phone header's overflow menu (`studio-more`) and its trailing item that releases the pin
     *  and scrolls to the page body (round 10, N14). Folded from `HEADER_COPY` (R10-M2 → parent). */
    moreLabel: 'More',
    volumePercent: (pct: number) => `${Math.round(pct)}%`,
    /** The music panel. */
    musicPanelTitle: 'Song',
    musicPanelMeta: (lengthMs: number) => `${clock(lengthMs)} long`,
    musicStartsAt: 'Starts at',
    musicFitToVideo: 'Fit to video',
    musicFitHint: 'Plays from where it starts to the end of your video.',
    musicFadeLength: 'Fade length',
    musicPlaying: (fromMs: number, toMs: number) => `Plays ${clockTenths(fromMs)} – ${clockTenths(toMs)} of the video`,
    /** The pane toggles (`studio-panel-toggle-files/-inspector`) and the collapsed rails' labels. */
    panelHide: (name: string) => `Hide ${name}`,
    panelShow: (name: string) => `Show ${name}`,
    inspectorHeading: 'Settings',
    /** The header's `studio-fullscreen` key. */
    fullscreenEnter: 'Fullscreen',
    fullscreenExit: 'Exit fullscreen',

    // Consolidated from the other round-4 slices' local copy consts (S5).
    /** G2 (B4): the timeline panel's sound chip when SOME video clips are muted. */
    soundChipSomeMuted: 'Clips’ sound · Some muted',
    /** G2 (B4): the tile chips' names. */
    clipMute: (clipNumber: number) => `Mute clip ${clipNumber}`,
    clipUnmute: (clipNumber: number) => `Unmute clip ${clipNumber}`,
    clipEdit: (clipNumber: number) => `Edit clip ${clipNumber}`,
    /** Round 6 (H6 UX#12): the tile's state chips, as a reader hears them and as the tooltip shows them. */
    clipStateMotion: (label: string) => `Motion: ${label}`,
    clipStateRotated: (degrees: number) => `Rotated ${degrees}°`,
    clipStateFlipped: 'Flipped',
    clipStateTransition: (label: string) => `Transition in: ${label}`,
    clipStateTrimmed: 'Trimmed',
    /** Round 6 (S2): the reserved tile while its file is still on its way. */
    clipPreparing: (percent: number) => (percent > 0 ? `Uploading ${Math.floor(percent)} %` : 'Preparing…'),
    /** Round 6 (S4): the song runs past the video and the user trimmed it there by hand. */
    musicOverrun: 'Longer than the video — it stops when the video ends.',
    /** G2 (B3): the song's two trim handles. */
    musicTrimStart: 'Trim the start of the music',
    musicTrimEnd: 'Trim the end of the music · pull it to the video’s end to fit',
    /** G1 (A3): the export pre-flight block's one action. */
    fixIt: 'Fix it',

    // Round 5 (parent): H3's feedback strings, consolidated from the slices' local consts.
    /** The tray row's phase line while a file uploads: "Uploading · 43 %". */
    trayPhaseUploading: (percent: number) => `Uploading · ${Math.max(0, Math.min(100, Math.floor(percent)))} %`,
    trayPhasePreparing: 'Preparing preview…',
    trayPhaseReady: 'Ready',
    /**
     * S1-10 (review 7): the refusal of a pick, as ONE sentence for the tray row and the live region -- the empty
     * files in the Studio's own "We couldn't read" words (never resumable.js's modal "larger than 1 bytes"), then
     * the type refusal as before.
     */
    pickRefusal: (r: { names: string[]; reason: string; empty?: string[] }) => {
        const parts: string[] = [];
        const empty = r.empty ?? [];
        if (empty.length === 1) parts.push(`We couldn’t read ${empty[0]} — the file is empty.`);
        else if (empty.length > 1) parts.push(`We couldn’t read ${empty.join(', ')} — the files are empty.`);
        if (r.names.length > 0) parts.push(COPY_TRAY_REJECTED(r.names, r.reason));
        return parts.join(' ');
    },
    /** The refused-TYPE line; `reason` is the uploader's own sentence. */
    trayRejected: (names: string[], reason: string) => COPY_TRAY_REJECTED(names, reason),
    /** The render sheet's elapsed clock. */
    renderElapsed: (clock: string) => `Elapsed ${clock}`,
    /** Round 8 (K1): the stage's honest expired chip -- L1 #12: `EXPIRED_COPY.clip`. */
    previewExpired: EXPIRED_COPY.clip,
    /**
     * Round 9 (L1 #1): a restored video clip whose playable body the edge will not give (its
     * `preview` refused for good) draws its poster -- and SAYS so while it is on stage, instead of
     * standing still for 30 s with no word (R8 QA #1).
     */
    // F3k (review 7): "after a reload" promised nothing the user could do (a reload did not bring it back in the owner's
    // browser); the note says what is true -- this browser shows the still, the export has the clip moving.
    previewNoMotion: 'This clip’s preview won’t play in this browser — it’s still in your export.',
    /**
     * bg-alpha M1: an animated transparent image whose packed preview this browser cannot composite (no WebGL, or a
     * driver that refused the shader). The stage shows its first frame on the chosen colour instead of the raw
     * picture-beside-matte body -- and says so.
     */
    previewAlphaStill: 'This browser can’t play a transparent animation here — showing its first frame. Your video will move.',
    /**
     * Review 6 (BUG-01): the in-session twin of `previewNoMotion` — this clip's own file is still in
     * hand, its element errored, and its POSTER drew from the edge, which is evidence the edge can
     * read the file. That is the only place the old "exports fine" claim was ever supported, so it
     * keeps it (in `previewNoMotion`'s words) while `proxyFailed` gives it up.
     */
    previewNoPlayback: 'This clip won’t play here — it’s still in your export.',
    /** Round 9 (L1 #4): the stage while a playing clip's element is stuck fetching (`STALL_MS`). */
    previewBuffering: 'Buffering…',
    /**
     * Said once (live region) per clip whose sound this browser cannot play. Round 12 (O1 ruling):
     * the same sentence as the inspector's ONE line (`soundExportOnlyLine`); the round-10 stage chip
     * and timeline-chip variants are gone -- a clip with sound plays WITH sound, from whichever
     * source has it, and this line survives only for a clip nothing can decode.
     */
    previewSilentClip: (name: string) =>
        `${name ? `${name}: ` : ''}this browser can’t play this clip’s sound — your export includes it.`,
} as const;

export type StudioCopy = typeof COPY;
// Round 8 (K1): expiry / Start over / one song -- the tab's copy, kept as a named table because 40+ call
// sites and specs address it by key.
export const R8_COPY = Object.freeze({
    /** S3: the toast when the page's last project was swept on open (L1 #12: `EXPIRED_COPY.project`). */
    projectExpired: EXPIRED_COPY.project,
    /** S3: the toast when files expire under a tab that stayed open (L1 #12: `EXPIRED_COPY.files`). */
    filesExpired: EXPIRED_COPY.files,
    startOver: 'Start over',
    startOverTitle: 'Start over?',
    startOverBody: 'This clears the timeline and the files staged in this browser. Videos you already exported stay on Downloads.',
    startOverConfirm: 'Yes, start over',
    startOverCancel: 'Keep editing',
    startedOver: 'Started a new project.',
    /** O6: what the server accepts today (`CompositionParser` / the schema's `AudioTrack.clips.maxItems: 1`). */
    oneSong: 'One song per video for now.',
});

// Round 8 (K1): the Files tray's round-8 rows.
export const TRAY_COPY = Object.freeze({
    /** O5: the "+" key's name; the visible label is the glyph. */
    addToTimeline: (name: string) => `Add ${name} to timeline`,
    addToTimelineTitle: 'Add to timeline',
    tryAgainTitle: 'Try again',
    /** O3 (S2): the row while the edge makes the browser-playable preview. The percentage is an estimate. */
    converting: (percent: number) => `Converting for preview · ${Math.max(0, Math.min(100, Math.floor(percent)))} %`,
    /** O1 (S3): the honest expired row -- never "exports fine" for bytes the edge has deleted (L1 #12: `EXPIRED_COPY`). */
    expiredClip: EXPIRED_COPY.clip,
    expiredTray: EXPIRED_COPY.tray,
});
